import React, { useEffect, useState } from "react";
import { api, fmtUSD, fmtCompact, fmtPct } from "../lib/api";
import {
  ShieldCheck,
  ShieldWarning,
  ArrowSquareOut,
  Coins,
  RocketLaunch,
  HeartStraight,
} from "@phosphor-icons/react";

/**
 * Impact & Outcomes — the marquee banner for the Outcome-as-a-Service pitch.
 *
 *   ┌───────────────────────────────────────────────┬───────────────────────────┐
 *   │ TOTAL REALIZED SAVINGS · RISK-ADJUSTED  ✓ 11/11 │  PROTECTIVE IMPACT       │
 *   │                                                 │                          │
 *   │      $51,114,172                                │  • Margin Protection     │
 *   │      ▸ drill into 180 realized awards           │  • Revenue Equivalence   │
 *   │                                                 │  • Growth Runway         │
 *   └───────────────────────────────────────────────┴───────────────────────────┘
 */

// Protective Impact translation defaults
const ASSUMED_MARGIN = 0.20;
const REVENUE_MULTIPLE = 1 / ASSUMED_MARGIN;        // 5×
const ASSUMED_DAILY_BURN = 219_178;                 // ~$80M annual opex

export default function AggregatedValueBoard({ summary, onNavigate }) {
  const [integrity, setIntegrity] = useState(null);

  useEffect(() => {
    api.integrity().then(setIntegrity).catch(() => setIntegrity({ verified: false, passed: 0, total: 11 }));
  }, []);

  if (!summary || !integrity) {
    return (
      <section
        data-testid="aggregated-value-board-loading"
        className="bg-slate-900 border border-slate-800 h-[320px] skeleton"
      />
    );
  }

  const k = summary.kpis;
  const realized       = k.risk_adjusted_savings || 0;
  const grossRealized  = k.realized_savings || 0;
  const realizedPct    = k.risk_adjusted_savings_pct || 0;

  // Protective Impact translation
  const margin       = realized;
  const revenueEquiv = margin * REVENUE_MULTIPLE;
  const runwayDays   = margin / ASSUMED_DAILY_BURN;

  const verified = !!integrity.verified;

  return (
    <section
      data-testid="aggregated-value-board"
      className="bg-slate-800 border border-slate-800 grid grid-cols-1 lg:grid-cols-5 gap-px"
    >
      {/* ─── HERO · Total Realized Savings (Risk-Adjusted) ─────────────────── */}
      <div className="lg:col-span-3 bg-slate-950 p-5 lg:p-6 relative overflow-hidden">
        <div className="flex items-baseline justify-between gap-4">
          <div className="tracking-mono-label text-[10px] text-slate-500">
            Total Realized Savings · Risk-Adjusted · TTM
          </div>
          <CfoInlineBadge verified={verified} passed={integrity.passed} total={integrity.total} />
        </div>

        <button
          data-testid="aggregated-realized-drilldown"
          onClick={() => onNavigate?.("opportunities", { stage: "ACT" })}
          className="group relative mt-3 text-left w-full"
          title="Drill into the Opportunities Ledger · ACT stage"
        >
          <div
            data-testid="aggregated-realized-value"
            className="font-display font-bold text-slate-50 leading-[0.98] tracking-tight
                       text-4xl sm:text-5xl
                       transition-colors group-hover:text-slate-200"
            style={{ letterSpacing: "-0.02em" }}
          >
            {fmtUSD(realized)}
          </div>
          <div className="mt-3 flex items-center gap-3 text-xs font-mono text-slate-400 flex-wrap">
            <span>
              Gross <span className="text-slate-200">{fmtCompact(grossRealized)}</span> × (100 − risk)/100
            </span>
            <span className="text-slate-700">·</span>
            <span className="text-slate-200">{fmtPct(realizedPct, 2)} of baseline</span>
            <span className="text-slate-700">·</span>
            <span>{k.total_pos} POs · {summary.direct.po_count + summary.indirect.po_count} suppliers in scope</span>
          </div>

          <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 border border-slate-700
                          bg-slate-900 text-slate-200 rounded-sm font-mono text-[11px]
                          tracking-mono-label group-hover:border-slate-500 transition-colors">
            <ArrowSquareOut weight="bold" size={12} />
            drill into {k.total_pos} realized awards
          </div>
        </button>
      </div>

      {/* ─── PROTECTIVE IMPACT · refined sub-grid on the right ─────────────── */}
      <div
        className="lg:col-span-2 bg-slate-950 p-5 lg:p-6 flex flex-col gap-3"
        data-testid="impact-protective-rail"
      >
        <div className="flex items-center justify-between">
          <div className="tracking-mono-label text-[10px] text-slate-500">
            Protective Impact · top-line · growth · revenue
          </div>
          <HeartStraight weight="duotone" className="text-slate-400" size={14} />
        </div>

        <ImpactRow
          icon={Coins}
          label="Revenue Equivalence"
          value={fmtUSD(revenueEquiv)}
          sub={`${fmtCompact(margin)} of savings ≡ ${fmtCompact(revenueEquiv)} of new revenue at ${Math.round(ASSUMED_MARGIN * 100)}% margin`}
          testid="impact-revenue-equiv"
        />
        <ImpactRow
          icon={RocketLaunch}
          label="Growth Runway"
          value={`+${Math.round(runwayDays).toLocaleString()} days`}
          sub={`extends operating runway at $${(ASSUMED_DAILY_BURN / 1000).toFixed(0)}K daily burn`}
          testid="impact-runway"
        />

        <div className="mt-auto pt-2 border-t border-slate-800 font-mono text-[10px] text-slate-500">
          assumption · {Math.round(ASSUMED_MARGIN * 100)}% operating margin · ${(ASSUMED_DAILY_BURN / 1000).toFixed(0)}K/day burn
        </div>
      </div>
    </section>
  );
}

// ─── Refined Protective Impact row ─────────────────────────────────────────
function ImpactRow({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="flex items-start gap-3" data-testid={testid}>
      <Icon weight="duotone" className="text-slate-400 mt-1 shrink-0" size={16} />
      <div className="min-w-0">
        <div className="tracking-mono-label text-[9px] text-slate-500">{label}</div>
        <div className="font-mono text-2xl text-slate-50 mt-0.5 leading-tight">
          {value}
        </div>
        <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">{sub}</p>
      </div>
    </div>
  );
}

// ─── Inline CFO Insurance Badge ────────────────────────────────────────────
function CfoInlineBadge({ verified, passed, total }) {
  if (verified) {
    return (
      <div
        data-testid="aggregated-cfo-badge"
        className="verified-pulse flex items-center gap-2 px-2.5 py-1 rounded-sm
                   border border-emerald-500/40 bg-emerald-500/10"
        title="Ledger reconciles to integrity assertions"
      >
        <ShieldCheck weight="fill" className="text-emerald-400" size={14} />
        <span className="font-mono text-[10px] text-emerald-200 tracking-mono-label">
          Verified · {passed}/{total}
        </span>
      </div>
    );
  }
  return (
    <div
      data-testid="aggregated-cfo-badge"
      className="flex items-center gap-2 px-2.5 py-1 rounded-sm border border-amber-500/40 bg-amber-500/10"
    >
      <ShieldWarning weight="fill" className="text-amber-400" size={14} />
      <span className="font-mono text-[10px] text-amber-200 tracking-mono-label">
        Drift · {passed}/{total}
      </span>
    </div>
  );
}
